"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = ({
  className,
  sideOffset = 4,
  variant = "default",
  style,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: "default" | "markdown";
}) => (
  <TooltipPrimitive.Content
    sideOffset={sideOffset}
    collisionPadding={8}
    className={cn(
      "z-50 overflow-hidden rounded-md border border-slate-normal bg-slate-subtle px-3 py-1.5 text-sm text-slate-normal shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      "max-h-(--radix-tooltip-content-available-height) overflow-y-auto",
      variant === "default" && " max-w-[200px]",
      className,
    )}
    style={{
      ...style,
      maxWidth:
        variant === "markdown" ? "min(60ch, calc(100vw - 32px))" : undefined,
    }}
    {...props}
  />
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

function SimpleTooltip({
  children,
  message,
  side,
  sideOffset,
  asChild = true,
  contentClassName,
  variant,
  open,
  onOpenChange,
  ...props
}: React.ComponentProps<"button"> & {
  message: React.ReactNode;
  side?: TooltipPrimitive.TooltipContentProps["side"];
  sideOffset?: number;
  asChild?: boolean;
  contentClassName?: string;
  variant?: "default" | "markdown";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Tooltip open={open} onOpenChange={onOpenChange}>
      <TooltipTrigger
        className="pointer-events-auto"
        data-tooltip-trigger
        asChild={asChild}
        {...props}
      >
        {children}
      </TooltipTrigger>
      <TooltipPrimitive.TooltipPortal>
        <TooltipContent
          side={side}
          sideOffset={sideOffset}
          className={contentClassName}
          variant={variant}
        >
          <p className="whitespace-pre-wrap">{message}</p>
        </TooltipContent>
      </TooltipPrimitive.TooltipPortal>
    </Tooltip>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  SimpleTooltip,
};
